import React, { useEffect, useState, useContext } from "react";
import {
  Spinner,
  Alert,
  Container,
  Card,
  Form,
  Button,
  Modal,
} from "react-bootstrap";
import { InputGroup, InputGroupText, Input, FormGroup } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
import { hasPermission, PERMISSIONS } from "utils/permissions";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import BootstrapTable from "react-bootstrap-table-next";
import HusariaHeader from "views/pages/components/HusariaHeader";

const DialplanPermissionsTable = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const userData = React.useMemo(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  }, []);

  const title = "Dialplan Permissions";
  const parentName = "HUCS";
  const addRoute = "/admin/hucs/add/dialplan-permission";
  const editBaseRoute = "/admin/hucs/edit/dialplan-permission";
  const rowKey = "dialplan_permission_uuid";

  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);

  const canDelete = hasPermission(userData, PERMISSIONS.DELETE);
  const canEdit = hasPermission(userData, PERMISSIONS.EDIT);
  const navigate = useNavigate();

  const fetchRows = async () => {
    setLoading(true);
    try {
      const currentSite = JSON.parse(localStorage.getItem("selectedSite"));
      const site_uuid = currentSite?.site_uuid || null;
      const response = await fetch(`${serverHost}/get_dialplan_permissions/${site_uuid}`);
      if (!response.ok) throw new Error("Failed to fetch dialplan permissions");
      const data = await response.json();
      setRows(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const pagination = paginationFactory({
    page: 1,
    alwaysShowAllBtns: true,
    showTotal: true,
    withFirstAndLast: false,
    sizePerPageRenderer: ({ onSizePerPageChange }) => (
      <div className="dataTables_length">
        <label>
          Show{" "}
          <select
            className="form-control form-control-sm"
            onChange={(e) => onSizePerPageChange(e.target.value)}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>{" "}
          entries.
        </label>
      </div>
    ),
  });

  const selectRow = {
    mode: "checkbox",
    clickToSelect: false,
    selected: selectedRows,
    onSelect: (row, isSelect) => {
      setSelectedRows((prev) =>
        isSelect
          ? [...prev, row[rowKey]]
          : prev.filter((id) => id !== row[rowKey])
      );
    },
    onSelectAll: (isSelect, rows) => {
      setSelectedRows(isSelect ? rows.map((r) => r[rowKey]) : []);
    },
    classes: "checkbox-column",
    headerClasses: "checkbox-column-header",
  };

  const columns = [
    {
      dataField: "permission_name",
      text: "Name",
      sort: true,
      headerAlign: "center",
      align: "center",
      formatter: (cell) => <span style={{ fontWeight: 600 }}>{cell ?? "-"}</span>,
    },
    {
      dataField: "extensions",
      text: "Extensions",
      sort: false,
      headerAlign: "center",
      align: "center",
      formatter: (cell) => {
        if (!Array.isArray(cell) || cell.length === 0) return <span>-</span>;
        const preview = cell.slice(0, 3).join(", ");
        const remainder = cell.length - 3;
        return (
          <span>
            {preview}
            {remainder > 0 && (
              <span className="text-muted ms-1">(+{remainder} more)</span>
            )}
          </span>
        );
      },
    },
    {
      dataField: "permission_categories",
      text: "No. of Permissions",
      sort: true,
      headerAlign: "center",
      align: "center",
      formatter: (cell) => (
        <span>{Array.isArray(cell) ? cell.length : 0}</span>
      ),
    },
    {
      dataField: "actions",
      text: "Actions",
      headerAlign: "center",
      align: "center",
      formatter: (_, row) => (
        <Button
          variant="danger"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            toggleDeleteModal(row);
          }}
        >
          Delete
        </Button>
      ),
    },
  ];

  const rowEvents = {
    onClick: (e, row) => {
      if (canEdit) navigate(`${editBaseRoute}/${row[rowKey]}`);
    },
  };

  const filteredRows = React.useMemo(() => {
    if (!search) return rows;
    return rows.filter((row) =>
      String(row.permission_name ?? "").toLowerCase().includes(search.toLowerCase())
    );
  }, [rows, search]);

  const toggleDeleteModal = (row = null) => {
    setRowToDelete(row);
    setDeleteModal(!deleteModal);
  };

  const handleDelete = async () => {
    if (!rowToDelete) return;
    try {
      const response = await fetch(
        `${serverHost}/delete_dialplan_permission/${rowToDelete[rowKey]}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!response.ok) throw new Error("Delete failed");
      await response.json();
      toggleDeleteModal();
      fetchRows();
    } catch (err) {
      alert(`Error deleting row: ${err.message}`);
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(
        selectedRows.map((id) =>
          fetch(`${serverHost}/delete_dialplan_permission/${id}`, {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
          })
        )
      );
      setSelectedRows([]);
      setDeleteModal(false);
      fetchRows();
    } catch (err) {
      alert(`Error deleting rows: ${err.message}`);
    }
  };

  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" variant={isDark ? "light" : "primary"} />
        <p className="mt-2" style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
          Loading {title.toLowerCase()}...
        </p>
      </div>
    );

  if (error)
    return (
      <Alert variant="danger" className="mt-5 text-center">
        {error}
      </Alert>
    );

  return (
    <>
      <HusariaHeader name={title} parentName={parentName} padding="4.5rem" />

      <Container fluid className="mt--6">
        <Card className={`shadow-sm ${isDark ? "bg-dark text-light" : ""}`}>
          <Card.Header
            style={{
              backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
              color: isDark ? "#e2e8f0" : "#111",
            }}
            className="d-flex justify-content-between align-items-center"
          >
            <div className="d-flex align-items-center">
              <Form className="navbar-search navbar-search-light form-inline">
                <FormGroup className="mb-0">
                  <InputGroup className="input-group-alternative input-group-merge search-border">
                    <InputGroupText>
                      <i
                        className="fas fa-search search-icon"
                        style={{ color: isDark ? "#b0b0b0" : "rgb(102, 102, 102)" }}
                      />
                    </InputGroupText>
                    <Input
                      className="form-control table-search-bar"
                      placeholder="Search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </InputGroup>
                </FormGroup>
              </Form>
            </div>

            <div className="d-flex gap-2">
              {canDelete && selectedRows.length > 0 && (
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => setDeleteModal(true)}
                >
                  Delete Selected ({selectedRows.length})
                </Button>
              )}
              <Button variant="info" size="sm" onClick={fetchRows}>
                Refresh
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate(addRoute)}
              >
                + Add Permission
              </Button>
            </div>
          </Card.Header>

          <Card.Body
            style={{ padding: 0, backgroundColor: isDark ? "#1f2937" : "#fff" }}
          >
            <div style={{ overflowX: "auto" }}>
              <ToolkitProvider
                keyField={rowKey}
                data={filteredRows}
                columns={columns}
              >
                {(props) => (
                  <div className="py-4 table-responsive">
                    <BootstrapTable
                      {...props.baseProps}
                      bootstrap4
                      pagination={pagination}
                      selectRow={canDelete ? selectRow : undefined}
                      bordered
                      hover
                      rowEvents={rowEvents}
                      rowStyle={{ cursor: canEdit ? "pointer" : "default" }}
                      classes={`align-items-center table-flush ${isDark ? "table-dark" : ""}`}
                      noDataIndication={() =>
                        loading ? (
                          <div className="text-center">Loading...</div>
                        ) : (
                          <div className="text-center">No dialplan permissions found.</div>
                        )
                      }
                    />
                  </div>
                )}
              </ToolkitProvider>
            </div>
          </Card.Body>
        </Card>
      </Container>

      {/* Delete Confirmation Modal */}
      <Modal show={deleteModal} onHide={toggleDeleteModal}>
        <Modal.Header
          closeButton
          style={{ backgroundColor: isDark ? "#1f2937" : "#fff" }}
        >
          <Modal.Title style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
            Delete Permission{selectedRows.length > 1 ? "s" : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            backgroundColor: isDark ? "#1f2937" : "#fff",
            color: isDark ? "#e2e8f0" : "#212529",
          }}
        >
          {selectedRows.length > 1
            ? `Are you sure you want to delete ${selectedRows.length} permissions? This action cannot be undone.`
            : "Are you sure you want to delete this permission? This action cannot be undone."}
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: isDark ? "#111827" : "#f8f9fa" }}>
          <Button variant="light" onClick={toggleDeleteModal}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={selectedRows.length > 1 ? handleBulkDelete : handleDelete}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DialplanPermissionsTable;